<template>
  <v-card class="candidate edit experience">
    <v-toolbar
      @click="show = !show"
      style="cursor: pointer"
      class="subheading pb-3 my-2"
      color="white"
      height="70"
      flat
    >
      <v-toolbar-side-icon class="pl-2">
        <v-avatar v-if="experience.organization" size="50">
          <img
            :src="
              experience.organization.logo ||
                '/img/missing/logos/organizations/original.png'
            "
          />
        </v-avatar>
      </v-toolbar-side-icon>
      <v-toolbar-title>
        <div class="pt-2">
          <div v-if="!experience.id">New Work Experience</div>
          <div v-else-if="none">No Work Experience</div>
          <div v-else-if="experience.position">{{ experience.position }}</div>
          <div
            v-if="experience.organization"
            :class="themes.candidate.light.mutedText"
            class="caption"
          >
            {{ experience.organization.name }}
          </div>
        </div>
      </v-toolbar-title>
      <v-spacer />
      <div v-if="isApprovedExperience" class="mr-2 mt-2">
        <v-tooltip left>
          <img
            slot="activator"
            src="/img/verified.png"
            width="24"
            height="24"
          />
          <span>
            Verified on
            {{ new Date(approvedVerifiedAt) | date("d MMMM yyy") }}
          </span>
        </v-tooltip>
      </div>
      <v-icon v-if="!none">
        {{ show ? "mdi-chevron-up" : "mdi-chevron-down" }}
      </v-icon>
    </v-toolbar>

    <v-slide-y-transition>
      <div v-if="show && !none">
        <v-form v-model="valid">
          <v-card-text class="pb-0">
            <OrganizationSelect
              v-model="modified.organization"
              :rules="rules.organization"
              :disabled="isVerifiedExperience"
              :toFront="false"
            >
              <template slot="selection" slot-scope="{ organization }">
                <div>{{ organization.name }}</div>
              </template>
            </OrganizationSelect>

            <v-select
              v-if="modified.organization.id && !modified.organization.industry"
              label="Industry"
              v-model="modified.organization.industry"
              :items="industries"
              :disabled="isVerifiedExperience"
              :hint="
                `This is the primary industry of ${modified.organization.name}`
              "
              :color="themes.candidate.light.input"
              class="candidate profile edit experience industry"
              attach=".candidate.profile.edit.experience.industry"
            />

            <v-text-field
              label="Position or Title"
              v-model="modified.position"
              :color="themes.candidate.light.input"
              :rules="rules.position"
              :disabled="isVerifiedExperience"
              required
            />

            <v-select
              label="Experience Type"
              v-model="modified.type"
              :color="themes.candidate.light.input"
              :rules="rules.type"
              :disabled="isVerifiedExperience"
              :items="types"
              dense
              required
              class="candidate profile edit experience type"
              attach=".candidate.profile.edit.experience.type"
            />

            <date-picker
              label="From"
              v-model="modified.startsAt"
              :color="themes.candidate.light.input"
              :theme="themes.candidate.light"
              :disabled="isVerifiedExperience"
              :rules="rules.startsAt"
              required
            />

            <date-picker
              label="To"
              v-model="modified.endsAt"
              :color="themes.candidate.light.input"
              :theme="themes.candidate.light"
              :disabled="!canEditEndsAt"
              :rules="rules.endsAt"
              hint="Future dates are allowed"
              placeholder="Current"
              clearable
            />

            <v-autocomplete
              label="Disciplines applied"
              v-model="modified.disciplines"
              :color="themes.candidate.light.input"
              :items="disciplines"
              item-text="name"
              item-value="id"
              return-object
              multiple
              chips
              attach
            />
          </v-card-text>

          <v-card-text class="pt-0" v-if="shouldPromptForResponsibilities">
            <v-combobox
              label="Responsibilities"
              v-model="modified.responsibilities"
              :color="themes.candidate.light.input"
              :disabled="isVerifiedExperience"
              :items="modified.responsibilities"
              multiple
              chips
              attach
            />
          </v-card-text>

          <v-card-text v-if="verifying" class="pt-0">
            <div class="subtitle-1 mb-2">Supervisor details</div>

            <v-alert :value="true" class="mb-3" outline type="warning">
              After adding your supervisor's details,
              <strong>you will not be able to make changes</strong>.
              Please double-check your information and confirm that you have
              permission to provide these contact details.
            </v-alert>

            <v-text-field
              label="Supervisor name"
              v-model="supervisor.name"
              :color="themes.candidate.light.input"
              :rules="rules.supervisorName"
              required
            />

            <v-text-field
              label="Supervisor position"
              v-model="supervisor.position"
              :color="themes.candidate.light.input"
              :rules="rules.supervisorPosition"
              required
            />

            <v-text-field
              label="Supervisor email address"
              v-model="supervisor.email"
              :color="themes.candidate.light.input"
              :rules="rules.supervisorEmail"
              required
            />

            <v-checkbox
              v-model="supervisorPermissionConfirmed"
              :color="themes.candidate.light.input"
            >
              <template slot="label">
                I confirm that I have permission to provide these contact
                details.
              </template>
            </v-checkbox>
          </v-card-text>

          <v-card-text
            v-else-if="experience.supervisors && experience.supervisors.length"
            class="pt-0"
          >
            <v-divider class="my-3" />

            <v-layout
              v-for="supervisor in experience.supervisors"
              :key="supervisor.id"
              align-center
              class="mb-2"
            >
              <v-flex xs8>
                <div class="font-weight-medium">{{ supervisor.name }}</div>
                <div :class="themes.candidate.light.mutedText" class="caption">
                  <span>{{ supervisor.position }}</span> &nbsp;
                  <span>&laquo;{{ supervisor.email }}&raquo;</span>
                </div>
                <div v-if="supervisor.feedback" class="mt-1">
                  <span class="caption font-italic">Feedback:</span>
                  <span class="caption"> {{ supervisor.feedback }} </span>
                </div>
              </v-flex>
              <v-flex xs4 class="text-xs-right">
                <v-chip
                  small
                  label
                  text-color="white"
                  :color="supervisorStatusColor(supervisor)"
                >
                  {{ supervisorStatus(supervisor) }}
                </v-chip>
              </v-flex>
            </v-layout>
          </v-card-text>

          <v-card-actions>
            <span
              v-if="isVerifiedExperience"
              class="px-2 grey--text text--darken-1 caption d-flex align-center"
            >
              <v-icon small class="mr-1" :color="themes.candidate.light.muted">
                mdi-information-outline
              </v-icon>
              <span>
                Updating experiences is limited once verification has commenced.
              </span>
            </span>
            <v-tooltip
              v-else-if="!verifying && experience.id"
              right
              :color="themes.candidate.light.tooltip"
            >
              <v-btn
                flat
                slot="activator"
                :color="themes.candidate.light.success"
                @click.stop="startVerifying"
              >
                Verify
              </v-btn>
              <span>
                Request that a supervisor verify your experience by providing
                their contact details
              </span>
            </v-tooltip>
            <v-spacer />
            <v-menu
              :close-on-content-click="false"
              nudge-top="30"
              left
              v-if="modified.id && !isVerifiedExperience && !verifying"
            >
              <v-btn
                flat
                slot="activator"
                :color="themes.candidate.light.danger"
              >
                Delete
              </v-btn>
              <v-card toolbar>
                <v-toolbar card :color="themes.candidate.light.dialogTitle">
                  Are you sure?
                  <v-spacer />
                  <v-btn
                    @click="deleteExperience"
                    :color="themes.candidate.light.danger"
                    :saving="saving"
                    dark
                  >
                    Delete
                  </v-btn>
                </v-toolbar>
              </v-card>
            </v-menu>

            <v-btn :color="themes.candidate.light.cancel" @click="cancel" flat>
              Cancel
            </v-btn>
            <template v-if="!verifying">
              <v-btn
                :color="themes.candidate.light.primaryActionButton"
                :disabled="!changes || !valid || saving"
                :loading="saving"
                @click="submit"
              >
                Save
              </v-btn>
            </template>
            <template v-else>
              <v-btn
                :color="themes.candidate.light.primaryActionButton"
                :disabled="!canSendVerification"
                :loading="saving"
                @click="verifyExperience"
              >
                Save &amp; send verification
              </v-btn>
            </template>
          </v-card-actions>
        </v-form>
      </div>
    </v-slide-y-transition>
  </v-card>
</template>

<script>
// Restricted supervisor email domains (mirrors API RestrictedEmailDomain::RESTRICTED_DOMAINS)
const RESTRICTED_SUPERVISOR_EMAIL_DOMAINS = [
  "10minutemail.com",
  "aol.com",
  "fastmail.com",
  "gmail.com",
  "hotmail.com",
  "icloud.com",
  "mail.com",
  "mail.ru",
  "maildrop.cc",
  "mailinator.com",
  "me.com",
  "outlook.com",
  "privaterelay.appleid.com",
  "protonmail.com",
  "tempmail.com",
  "throwaway.email",
  "trashmail.com",
  "tutanota.com",
  "yahoo.com",
  "yandex.com",
  "zoho.com"
];

// Components
import OrganizationSelect from "@/components/selects/ExperienceOrganizationSelect";

// Data
import industries from "@/data/industries";
import types from "@/data/experience_types";

// Helpers
import { mapGetters } from "vuex";

// Vue Component
export default {
  components: { OrganizationSelect },

  created() {
    this.$store.dispatch("disciplines/fetchAll");
  },

  props: {
    experience: { type: Object, required: true },
    profile: { type: Object, required: true }
  },

  data() {
    return {
      saving: false,
      modified: JSON.parse(JSON.stringify(this.experience)),
      show: !this.experience.id,
      valid: false,
      none: this.experience.type === "None",
      verifying: false,
      supervisor: {
        name: "",
        position: "",
        email: ""
      },
      supervisorPermissionConfirmed: false,
      rules: {
        position: [
          v => !!v || "must be specified",
          v => v.length < 65 || "must not exceed 64 characters"
        ],
        organization: [
          function(v) {
            return !!v || "must be specified";
          }
        ],
        type: [v => !!v || "must be specified"],
        startsAt: [
          v => !!v || "You must specify a starting date",
          v =>
            !this.experience.endsAt ||
            new Date(v) <= new Date(this.experience.endsAt) ||
            "Must be before To date"
        ],
        endsAt: [
          v =>
            !v ||
            new Date(v) >= new Date(this.experience.startsAt) ||
            "Must be after From date"
        ],
        supervisorName: [v => !!v || "must be specified"],
        supervisorPosition: [v => !!v || "must be specified"],
        supervisorEmail: [
          v => !!v || "must be specified",
          v => !v || /.+@.+\..+/.test(v) || "is not a valid email",
          v =>
            !v ||
            !this.isRestrictedSupervisorEmailDomain(v) ||
            "Domain is not allowed. Please use a company email address."
        ]
      },
      // Data
      industries,
      types
    };
  },
  computed: {
    ...mapGetters({
      disciplines: "disciplines/all"
    }),

    changes() {
      if (!_.get(this.experience, "id")) return true;

      return !_.isEqual(this.modified, this.experience);
    },

    responsibilities() {
      if (!this.modified.responsibilities) return [];

      return this.modified.responsibilities.map(r =>
        r.replace(/[^\x00-\xFF]/g, "")
      );
    },

    shouldPromptForResponsibilities() {
      // Only on the profile edit page
      return window.location.href.includes("profile");
    },

    isVerifiedExperience() {
      const supervisors = _.get(this.experience, "supervisors");
      if (!supervisors || !supervisors.length) return false;

      // Experience is treated as having active verification if it has any
      // supervisor without feedback (Pending or Approved)
      return supervisors.some(s => !s.feedback);
    },

    approvedSupervisor() {
      const supervisors = _.get(this.experience, "supervisors");
      if (!supervisors || !supervisors.length) return null;

      const approved = supervisors.filter(
        s => (s.verifiedAt || s.verified_at) && !s.feedback
      );
      if (!approved.length) return null;

      // Use the most recently verified supervisor for the badge
      return _.maxBy(approved, s => new Date(s.verifiedAt || s.verified_at));
    },

    isApprovedExperience() {
      return !!this.approvedSupervisor;
    },

    approvedVerifiedAt() {
      if (!this.approvedSupervisor) return null;
      return (
        this.approvedSupervisor.verifiedAt ||
        this.approvedSupervisor.verified_at
      );
    },

    canEditEndsAt() {
      // If not verified, end date is freely editable
      if (!this.isVerifiedExperience) return true;

      // Once verified, only allow setting an end date if one does not exist yet
      return !this.experience.endsAt;
    },

    canSendVerification() {
      return this.valid && !this.saving && this.supervisorPermissionConfirmed;
    }
  },

  methods: {
    cancel() {
      if (!_.get(this.experience, "id")) this.$emit("refresh");

      this.modified = JSON.parse(JSON.stringify(this.experience));
      this.verifying = false;
      this.resetSupervisorForm();
      this.show = false;
    },

    startVerifying() {
      this.verifying = true;
      this.resetSupervisorForm();
    },

    resetSupervisorForm() {
      this.supervisor = { name: "", position: "", email: "" };
      this.supervisorPermissionConfirmed = false;
    },

    deleteExperience() {
      this.$store
        .dispatch("candidate/experiences/destroy", this.experience.id)
        .then(
          () => this.$store.dispatch("snackbarSuccess", "Deleted"),
          () => this.$store.dispatch("snackbarError", "Failed to Delete")
        )
        .finally(() => {
          this.$emit("refresh");
        });
    },

    submit() {
      this.saving = true;
      const action = this.modified.id
        ? "candidate/experiences/update"
        : "candidate/experiences/create";

      return this.$store
        .dispatch(action, {
          ...this.modified,
          responsibilities: this.responsibilities
        })
        .then(
          () => this.$store.dispatch("snackbarSuccess", "Saved"),
          () => this.$store.dispatch("snackbarError", "Experience not saved")
        )
        .finally(() => {
          this.saving = false;
          this.$emit("refresh");
        });
    },

    verifyExperience() {
      if (!this.experience.id) return;

      this.saving = true;

      const payload = {
        ...this.modified,
        id: this.experience.id,
        responsibilities: this.responsibilities
      };

      this.$store
        .dispatch("candidate/experiences/update", payload)
        .then(() => {
          return Vue.$http.post(
            `candidate/experiences/${this.experience.id}/supervisors`,
            { supervisor: this.supervisor }
          );
        })
        .then(
          () =>
            this.$store.dispatch(
              "snackbarSuccess",
              "Verification request sent to supervisor"
            ),
          () =>
            this.$store.dispatch(
              "snackbarError",
              "Could not start supervisor verification"
            )
        )
        .finally(() => {
          this.saving = false;
          this.verifying = false;
          this.resetSupervisorForm();
          this.$emit("refresh");
        });
    },

    isRestrictedSupervisorEmailDomain(email) {
      if (!email) return false;

      const domain = email.split("@")[1]?.toLowerCase();

      if (!domain) return false;

      return RESTRICTED_SUPERVISOR_EMAIL_DOMAINS.includes(domain);
    },

    supervisorStatus(supervisor) {
      // Mirror backend scopes: rejected > approved > pending
      if (supervisor.feedback) return "Rejected";
      if (supervisor.verifiedAt || supervisor.verified_at) return "Approved";
      return "Pending";
    },

    supervisorStatusColor(supervisor) {
      const status = this.supervisorStatus(supervisor);

      if (status === "Approved") return this.themes.candidate.light.success;
      if (status === "Rejected") return this.themes.candidate.light.error;
      return this.themes.candidate.light.warning;
    }
  }
};
</script>
